import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ROLE_LABELS } from '../../types/permissions';

/**
 * Selector de empresa activa para usuarios multi-empresa.
 * Solo se muestra cuando el usuario pertenece a más de una empresa.
 *
 * La visibilidad por breakpoint la controla el contenedor padre (Header):
 *   - Desktop: dentro de un wrapper `hidden md:flex`.
 *   - Mobile: dentro del dropdown del menú hamburguesa (`md:hidden`).
 * Por eso el root NO tiene `hidden sm:` (antes lo ocultaba siempre en mobile,
 * incluso dentro del menú hamburguesa, y no había forma de cambiar de empresa)
 */
interface CompanySelectorProps {
  /** Clases extra para el contenedor (ej. `w-full justify-center` en mobile). */
  className?: string;
}

const CompanySelector: React.FC<CompanySelectorProps> = ({ className = '' }) => {
  const { userCompanies, activeEmpresaId, switchCompany } = useAuth();
  const [switching, setSwitching] = useState(false);

  if (userCompanies.length <= 1) return null;

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newEmpresaId = e.target.value;
    if (newEmpresaId === activeEmpresaId) return;
    setSwitching(true);
    try {
      await switchCompany(newEmpresaId);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'No se pudo cambiar de empresa.';
      console.error('[CompanySelector] Error switching:', err);
      // Surfacer el motivo (ej. hay cambios pendientes sin sincronizar offline).
      alert(msg);
    } finally {
      setSwitching(false);
    }
  };

  const current = userCompanies.find((c) => c.empresa_id === activeEmpresaId);

  return (
    <div className={`flex items-center gap-2 bg-white/10 rounded px-3 py-1 ${className}`}>
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-200" fill="none"
        viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
      <select
        value={activeEmpresaId ?? ''}
        onChange={handleChange}
        disabled={switching}
        title={current ? `Tu rol aquí: ${ROLE_LABELS[current.role]}` : ''}
        className="bg-transparent text-white text-sm font-medium focus:outline-none cursor-pointer disabled:opacity-50 max-w-[160px] truncate"
      >
        {userCompanies.map((c) => (
          <option key={c.empresa_id} value={c.empresa_id} className="text-gray-900">
            {c.empresa_nombre}
          </option>
        ))}
      </select>
      {switching && <span className="text-xs text-green-200">…</span>}
    </div>
  );
};

export default CompanySelector;
